import { Badge as MantineBadge, BadgeProps, useMantineTheme } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import { getContrastingTextColor } from '../../../utils/color';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedBadgeProps extends Omit<BadgeProps, 'variant'> {
  variant?: 'gradient' | 'filled' | 'light' | 'outline' | 'dot' | 'default';
  gradientVariant?: GradientVariant;
  outlined?: boolean;
  pulse?: boolean;
  /** Polymorphic component to render as (e.g., 'a', 'span', Link) */
  component?: any;
  /** Route path when using component prop with React Router Link */
  to?: string;
  /** Standard anchor href for direct links (when component="a") */
  href?: string;
  /** Target for anchor links (when component="a") */
  target?: string;
  /** Rel attribute for anchor links (when component="a") */
  rel?: string;
}

const Badge = forwardRef<HTMLDivElement, ThemedBadgeProps>(
  ({ variant = 'gradient', gradientVariant = 'primary', outlined = false, pulse = false, children, style, ...rest }, ref) => {
    const theme = useMantineTheme();
    const lineSpacing = usePreferencesStore((s) => s.lineSpacing);
    const letterSpacingPref = usePreferencesStore((s) => s.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    if (variant !== 'gradient') {
      return (
        <MantineBadge ref={ref} variant={variant} style={{ textAlign: 'left', ...accessibilityStyles, ...style }} {...rest}>
          {children}
        </MantineBadge>
      );
    }

    const gradient = theme.other.gradients[gradientVariant];

    // Calculate text color for gradient background
    const gradientBg = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    const explicitStyleColor =
      style && typeof style === 'object' && !Array.isArray(style) && 'color' in style
        ? (style as { color?: string }).color
        : undefined;

    if (outlined) {
      return (
        <MantineBadge
          ref={ref}
          variant="outline"
          style={{
            textAlign: 'left',
            borderImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to}) 1`,
            borderImageSlice: 1,
            backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: pulse ? 'badge-pulse 2s ease-in-out infinite' : undefined,
            ...accessibilityStyles,
            ...style,
          }}
          {...rest}
        >
          {children}
          {pulse && (
            <style>{`
              @keyframes badge-pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.7; }
              }
            `}</style>
          )}
        </MantineBadge>
      );
    }

    return (
      <MantineBadge
        ref={ref}
        style={{
          textAlign: 'left',
          backgroundImage: gradientBg,
          color: explicitStyleColor || autoTextColor,
          border: 'none',
          animation: pulse ? 'badge-pulse 2s ease-in-out infinite' : undefined,
          ...accessibilityStyles,
          ...style,
        }}
        {...rest}
      >
        {children}
        {pulse && (
          <style>{`
            @keyframes badge-pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
          `}</style>
        )}
      </MantineBadge>
    );
  }
);

Badge.displayName = 'Badge';

export default Badge;
